import React, { useContext, useEffect } from 'react';
import { Button } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Dinero from 'dinero.js';
import FacebookShareButton from '../../../components/FacebookShareButton';
import SellerGroup from '../../../utils/models/SellerGroup';
import StripeCheckout from '../../../components/StripeCheckout';

import { ReactComponent as SuccessIcon } from '../../../assets/icons/payment-success.svg';
import { OrderContext } from '../../../context/OrderContext';
import { isDefined } from '../../../utils/helpers';
import { OrderQuery } from '../../../generated/graphql';

const { REACT_APP_ROOT_URL } = process.env;

interface PaymentResultProps {
  order: NonNullable<OrderQuery['orderById']>
}

const PaymentResult = ({
  order: {
    _id: orderId, quantities, code, sellerGroup: sg, paymentStatus,
  },
} : PaymentResultProps) => {
  const { resetOrder } = useContext(OrderContext);
  const { slug } = useParams();
  const navigate = useNavigate();
  const [translate] = useTranslation();
  const { search } = useLocation();

  useEffect(() => {
    resetOrder();
  }, [resetOrder]);

  const isPaymentSuccessful = paymentStatus === 'succeeded';

  const sellerGroup = sg ? new SellerGroup(sg) : sg;

  const shareButtonLink = sellerGroup ? sellerGroup.orderLink(slug as string) : `${REACT_APP_ROOT_URL}/${slug}`;

  const amountPaid = quantities?.filter(isDefined).reduce((sum, { quantity, price }) => sum + ((quantity || 0) * price), 0);
  const formattedAmountPaid = Dinero({ amount: amountPaid }).toFormat();
  const amountPaidLabel = isPaymentSuccessful
    ? translate('COMPONENTS.PAYMENT_RESULT.AMOUNT_PAID')
    : translate('COMPONENTS.PAYMENT_RESULT.AMOUNT_TO_PAY');

  const quantitiesPreview = quantities?.filter(isDefined).map(({ article: { _id, name }, quantity }) => (
    <div key={_id}>
      <div className="d-flex justify-content-between">
        <h6 className="mb-0">{name}</h6>
        <h6 className="mb-0 ms-3">
          &times;
          {' '}
          {quantity}
        </h6>
      </div>
      <hr />
    </div>
  ));

  const redirectToShop = () => {
    const path = `/${slug + search}`;
    navigate(path);
  };

  const paymentMessage = isPaymentSuccessful
    ? (
      <>
        <SuccessIcon />
        <h3 className="text-success">
          {translate('COMPONENTS.PAYMENT_RESULT.PAYMENT_SUCCESSFUL')}
        </h3>
      </>
    )
    : (
      <>
        <h3 className="text-warning">
          {translate('COMPONENTS.PAYMENT_RESULT.PAYMENT_FAILED')}
        </h3>
        <StripeCheckout
          buttonLabel={translate('COMPONENTS.PAYMENT_RESULT.TRY_AGAIN')}
          orderId={orderId}
        />
      </>
    );

  return (
    <>
      <div className="text-center my-4">
        {paymentMessage}
      </div>
      <div>
        <p className="text-start">{translate('COMPONENTS.PAYMENT_RESULT.YOUR_ORDER')}</p>
        {quantitiesPreview}
      </div>
      <div>
        {isPaymentSuccessful && (
          <div className="d-flex align-items-center">
            <p className="mb-2 me-1">{translate('COMPONENTS.PAYMENT_RESULT.ORDER_NUMBER')}</p>
            <h6 className="mb-2">{code}</h6>
          </div>
        )}
        <div className="d-flex align-items-center">
          <p className="mb-2 me-1">{amountPaidLabel}</p>
          <h6 className="mb-2">{formattedAmountPaid}</h6>
        </div>
      </div>
      <div className="my-4">
        {isPaymentSuccessful && <FacebookShareButton id={orderId} link={shareButtonLink} />}
        <Button className="text-primary" onClick={redirectToShop}>
          {translate('COMMON.BACK_TO_SHOP')}
        </Button>
      </div>
    </>
  );
};

export default PaymentResult;
